// The enter/manage form: cost estimate for openrouter models, and the provider picker that
// swaps key hint, get-a-key link, model placeholder and suggestions. Only "custom" asks for a url.
import { track, trackEnterFunnel } from "./telemetry.ts";
import { $, esc } from "./dom.ts";

/** The key boxes are masked by css, not `type=password`: a password field makes Chrome's
 * password manager claim the text box before it (the model) as a username, and its
 * "manage passwords" bubble buries the model list. Where the css cannot mask, fall back.
 */
export function maskSecrets(root: ParentNode) {
  if (typeof CSS !== "undefined" && CSS.supports && CSS.supports("-webkit-text-security", "disc")) return;
  root.querySelectorAll("input.secret").forEach((el) => {
    (el as HTMLInputElement).type = "password";
  });
}

/** The reply budget and the message limit, as the server plays them (crates/basho-rules/src/tokens.rs). */
const REPLY_CAP = 500;
const MAX_MESSAGE_TOKENS = 250;
const MAX_TURNS = 50;

/**
 * A bout's worst-case cost in USD for one side: every one of its turns re-sends the whole
 * transcript so far (each earlier turn up to the message limit) plus the reply budget.
 * @param price USD per token, [prompt, completion]
 * @param turns total turns in the bout
 */
export const costPerBout = (price: [number, number], turns: number) => {
  const own = Math.ceil(turns / 2);
  let inp = 0;
  for (let k = 0; k < own; k++) inp += REPLY_CAP + 2 * k * MAX_MESSAGE_TOKENS;
  return inp * price[0] + own * MAX_MESSAGE_TOKENS * price[1];
};

const fmt = (x: number) => (x < 0.01 ? "<$0.01" : "$" + x.toFixed(2));

/**
 * The provider a key belongs to, read off its prefix (api/mod.rs says the same). Null when
 * the prefix says nothing: then the picker under "more" decides.
 */
export const providerFromKey = (key: string): string | null => {
  const k = key.trim();
  if (k.startsWith("sk-or-")) return "openrouter";
  if (k.startsWith("sk-ant-")) return "anthropic";
  if (k.startsWith("AIza")) return "google";
  if (k.startsWith("gsk_")) return "groq";
  if (k.startsWith("xai-")) return "xai";
  if (k.startsWith("sk-")) return "openai";
  return null;
};

/** the price table the page embeds */
const prices = (): PricesJson => {
  const el = $("[data-prices]");
  try {
    const v = JSON.parse(el?.textContent || "{}");
    return typeof v === "object" && v !== null ? v : {};
  } catch (_) {
    console.warn("bad price table");
    return {};
  }
};

/** whether a gift / invite key is currently active (restricting OpenRouter to frontier models) */
export function isGiftKeyActive(form: HTMLFormElement): boolean {
  const pick = form.querySelector("[data-key-pick]") as HTMLSelectElement | null;
  const keyInput = form.querySelector("[data-key]") as HTMLInputElement | null;
  if (!pick) return false;
  if (keyInput && keyInput.value.trim().length > 0) return false;
  const opt = pick.options[pick.selectedIndex];
  return !!opt && opt.dataset.gift === "1";
}

/** The model list: a select over the provider's models, driving the id box the server
 * reads. "other" shows the box; a current id not on the list starts there.
 */
function wireModelPick(form: HTMLFormElement, input: HTMLInputElement, provider: () => string, changed: () => void) {
  const pick = $("[data-model-pick]", form) as HTMLSelectElement | null;
  if (!pick) return () => {};
  const groups = Array.from(pick.querySelectorAll("optgroup")) as HTMLOptGroupElement[];
  const other = pick.querySelector("[data-other]") as HTMLOptionElement;
  groups.forEach((g) => g.remove());
  const showBox = (on: boolean, focus?: boolean) => {
    input.hidden = !on;
    input.required = on;
    if (on && focus) input.focus();
  };
  const rebuild = () => {
    const isGift = isGiftKeyActive(form);
    const note = form.querySelector("[data-gift-model-note]") as HTMLElement | null;
    if (note) note.hidden = !isGift;

    const p = provider();
    const want = p === "auto" ? "openrouter" : p;
    pick.querySelectorAll("optgroup").forEach((g) => g.remove());

    const frontierCsv = pick.dataset.frontierModels || "";
    const frontierSet = new Set(
      frontierCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    );

    const activeGroups: HTMLOptGroupElement[] = [];

    if (want === "openrouter") {
      const frontierGroup = groups.find((g) => g.dataset.for === "openrouter-frontier");
      const openrouterGroup = groups.find((g) => g.dataset.for === "openrouter");
      if (frontierGroup) {
        pick.insertBefore(frontierGroup, other);
        activeGroups.push(frontierGroup);
        frontierGroup.querySelectorAll("option").forEach((o) => {
          (o as HTMLOptionElement).disabled = false;
        });
      }
      if (openrouterGroup) {
        pick.insertBefore(openrouterGroup, other);
        activeGroups.push(openrouterGroup);
        openrouterGroup.querySelectorAll("option").forEach((o) => {
          const opt = o as HTMLOptionElement;
          if (isGift) {
            const allowed = frontierSet.has(opt.value);
            opt.disabled = !allowed;
            if (!allowed && !opt.textContent?.includes("(requires your own key)")) {
              opt.dataset.originalText = opt.textContent || "";
              opt.textContent = `${opt.textContent} (requires your own key)`;
            }
          } else {
            opt.disabled = false;
            if (opt.dataset.originalText) {
              opt.textContent = opt.dataset.originalText;
              delete opt.dataset.originalText;
            }
          }
        });
      }
      if (other) {
        other.disabled = isGift;
      }
    } else {
      const group = groups.find((g) => g.dataset.for === want);
      if (group) {
        pick.insertBefore(group, other);
        activeGroups.push(group);
        group.querySelectorAll("option").forEach((o) => {
          (o as HTMLOptionElement).disabled = false;
        });
      }
      if (other) {
        other.disabled = false;
      }
    }

    const listed =
      activeGroups.length > 0 &&
      activeGroups.some((g) =>
        Array.from(g.querySelectorAll("option")).some(
          (o) => (o as HTMLOptionElement).value === input.value && !(o as HTMLOptionElement).disabled,
        ),
      );

    if (listed) {
      pick.value = input.value;
      showBox(false);
    } else if (input.value) {
      pick.value = "other";
      showBox(true);
    } else {
      pick.value = "";
      showBox(false);
    }
  };
  pick.addEventListener("change", () => {
    input.setCustomValidity("");
    pick.setCustomValidity("");
    if (pick.value === "other") {
      input.value = "";
      showBox(true, true);
      return;
    }
    input.value = pick.value;
    showBox(false);
    changed();
  });
  input.addEventListener("input", () => {
    input.setCustomValidity("");
    pick.setCustomValidity("");
  });
  rebuild();
  return rebuild;
}

/** a pre-prompt's size as the counter shows it: characters, and tokens near enough (words × 4/3, as the server measures builds) */
export const budgetOf = (text: string) => ({
  chars: text.length,
  tokens: Math.round((text.split(/\s+/).filter(Boolean).length * 4) / 3),
});

/**
 * The enter form as three steps (#438): who, the pre-prompt, into the ring — one on screen at a time, each checked before the
 * next by the page itself (the form is novalidate: a hidden step's required field would otherwise stop the submit with the
 * browser's "is not focusable"), every field kept as it was when you step back. A refused entry (the server's error and the
 * draft) shows the three together, as does a page without js. Each step's view and completion go to the funnel.
 */
interface AltchaWidgetElement extends HTMLElement {
  getState?: () => string;
  verify?: () => void;
}

export function initFunnel(form: HTMLFormElement) {
  if (!form.hasAttribute("data-funnel") || form.dataset.refused) return;
  const steps = Array.from(form.querySelectorAll("fieldset[data-step]")).filter(
    (el): el is HTMLFieldSetElement => el instanceof HTMLFieldSetElement,
  );
  if (steps.length < 2) return;
  form.noValidate = true;
  form.querySelectorAll(".stepper, .stepnav, [data-back]").forEach((el) => {
    (el as HTMLElement).hidden = false;
  });
  let at = 0;
  const show = (i: number) => {
    at = i;
    steps.forEach((st, k) => {
      st.hidden = k !== i;
    });
    form.querySelectorAll("[data-stepper]").forEach((li, k) => {
      li.classList.toggle("current", k === i);
      li.classList.toggle("done", k < i);
    });
    track("enter_step_view", { step: i + 1 });
    const first = steps[i].querySelector("input:not([type=hidden]):not([hidden]), select, textarea");
    if (first instanceof HTMLElement && i > 0) first.focus();
  };
  /** the step's first field the page would refuse, told to the viewer (a hidden one, like the model's id box, through its picker) */
  const check = (i: number) => {
    if (i === 0) {
      const modelInput = steps[0].querySelector("[data-model]") as HTMLInputElement | null;
      const modelPick = steps[0].querySelector("[data-model-pick]") as HTMLSelectElement | null;
      if (modelInput && modelPick) {
        if (isGiftKeyActive(form)) {
          const frontierCsv = modelPick.dataset.frontierModels || "";
          const allowed = frontierCsv
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          const chosen = modelInput.value.trim();
          if (chosen && !allowed.includes(chosen)) {
            const msg = `invite keys are restricted to beta frontier models: ${allowed.join(", ")}. pick one of these models or use your own key in step 3.`;
            modelInput.setCustomValidity(msg);
            modelPick.setCustomValidity(msg);
          } else {
            modelInput.setCustomValidity("");
            modelPick.setCustomValidity("");
          }
        } else {
          modelInput.setCustomValidity("");
          modelPick.setCustomValidity("");
        }
      }
    }
    const bad = Array.from(steps[i].querySelectorAll("input, select, textarea")).find(
      (el) =>
        (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) &&
        !el.checkValidity(),
    );
    if (!bad) return true;
    const say = bad instanceof HTMLElement && bad.hidden ? steps[i].querySelector("[data-model-pick]") : bad;
    // a bad value under a folded "more" or "advanced" (#646): the fold opens so the browser can point at it
    const fold = say instanceof HTMLElement ? say.closest("details") : null;
    if (fold) fold.open = true;
    if (say instanceof HTMLInputElement || say instanceof HTMLSelectElement || say instanceof HTMLTextAreaElement)
      say.reportValidity();
    return false;
  };
  form.querySelectorAll("[data-next]").forEach((b) =>
    b.addEventListener("click", () => {
      if (!check(at)) return;
      track("enter_step_complete", { step: at + 1 });
      show(Math.min(steps.length - 1, at + 1));
    }),
  );
  form.querySelectorAll("[data-back]").forEach((b) => b.addEventListener("click", () => show(Math.max(0, at - 1))));

  // sync key requirement between saved key picker and new key input
  const pick = form.querySelector("[data-key-pick]");
  const key = form.querySelector("[data-key]");
  if (pick instanceof HTMLSelectElement && key instanceof HTMLInputElement) {
    const sync = () => {
      key.required = pick.value === "";
    };
    pick.addEventListener("change", sync);
    sync();
  }

  // the submit: every step checked, the first that fails shown; the proof of work solved where it stands at the door
  let powWaiting = false;
  form.addEventListener("submit", (e) => {
    for (let i = 0; i < steps.length; i++) {
      if (!check(i)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        show(i);
        check(i);
        return;
      }
    }
    const pow = form.querySelector("altcha-widget");
    const proof = form.querySelector('input[name="altcha"]');
    if (pow && !(proof instanceof HTMLInputElement && proof.value)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      trackEnterFunnel("fail", { step: "validation", error_code: "missing_altcha" });
      if (pow instanceof HTMLElement) pow.scrollIntoView({ block: "center" });

      let powStatus = form.querySelector("[data-pow-status]");
      if (!powStatus) {
        powStatus = document.createElement("p");
        powStatus.className = "dim small";
        powStatus.setAttribute("data-pow-status", "");
        powStatus.setAttribute("aria-live", "polite");
        pow.insertAdjacentElement("afterend", powStatus);
      }
      const widget = pow instanceof HTMLElement ? (pow as AltchaWidgetElement) : null;
      const state = widget?.getState ? widget.getState() : "unverified";
      if (state === "error") {
        if (powStatus instanceof HTMLElement) {
          powStatus.hidden = false;
          powStatus.className = "error";
          powStatus.textContent = "anti-bot check failed. please reload the page and try again.";
        }
        return;
      }
      if (powWaiting) return;
      powWaiting = true;

      if (powStatus instanceof HTMLElement) {
        powStatus.hidden = false;
        powStatus.className = "dim small";
        powStatus.textContent = "verifying anti-bot check… entering automatically once ready.";
      }
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn instanceof HTMLButtonElement) {
        submitBtn.disabled = true;
        submitBtn.textContent = "verifying…";
      }
      if (state === "unverified" && widget?.verify) {
        widget.verify();
      }
      const restoreBtn = () => {
        powWaiting = false;
        if (submitBtn instanceof HTMLButtonElement) {
          submitBtn.disabled = false;
          submitBtn.textContent = "enter the ring";
        }
      };
      const onVerified = () => {
        restoreBtn();
        if (powStatus instanceof HTMLElement) powStatus.hidden = true;
        if (typeof form.requestSubmit === "function") form.requestSubmit();
        else form.submit();
      };
      const onStateChange = (ev: Event) => {
        const detail = ev instanceof CustomEvent ? (ev.detail as { state?: string } | null) : null;
        if (detail && detail.state === "error") {
          pow.removeEventListener("verified", onVerified);
          restoreBtn();
          if (powStatus instanceof HTMLElement) {
            powStatus.hidden = false;
            powStatus.className = "error";
            powStatus.textContent = "anti-bot check failed. please reload the page and try again.";
          }
        }
      };
      pow.addEventListener("verified", onVerified, { once: true });
      pow.addEventListener("statechange", onStateChange, { once: true });
      return;
    }
    track("enter_step_complete", { step: steps.length });
  });
  // the pre-prompt's budget, live
  const area = form.querySelector("[data-budget]"),
    count = form.querySelector("[data-budget-count]");
  if (area instanceof HTMLTextAreaElement && count instanceof HTMLElement) {
    const max = +(count.dataset.max ?? 0) || area.maxLength;
    const say = () => {
      const b = budgetOf(area.value);
      count.textContent = b.chars
        ? `${b.chars} / ${max} characters · about ${b.tokens} tokens`
        : `up to ${max} characters: blank, and the model fights as itself`;
    };
    area.addEventListener("input", say);
    say();
  }
  show(0);
}

export function initForm() {
  const form = $("form[data-enter], form[data-practice]") as HTMLFormElement | null;
  if (!form) return;
  // the practice ring's form (#576) takes the same pickers and none of the entry funnel's telemetry
  const funnel = form.hasAttribute("data-enter");
  initFunnel(form); // first: its submit check stands before the listeners that count an entry
  const table = prices();
  const input = $("[data-model]", form) as HTMLInputElement | null;
  const est = $("[data-est]");
  const prov = $("select[data-provider]", form) as HTMLSelectElement | null;
  // "auto" until the key (pasted or picked) says which: then the picker follows the key
  const auto = () => !!prov && prov.dataset.auto === "1";
  const isOR = () => !prov || prov.value === "openrouter" || prov.value === "auto";
  const update = () => {
    if (!est || !input) return;
    if (!isOR()) {
      est.textContent = "cost estimate is only available for openrouter models";
      return;
    }
    const p = table[input.value.trim()];
    if (!p) {
      est.textContent = "estimated cost per bout: unknown model";
      return;
    }
    est.textContent = `estimated cost per bout: ~${fmt(costPerBout(p, 20))} typical, ${fmt(costPerBout(p, MAX_TURNS))} worst case (${MAX_TURNS} turns)`;
  };
  if (input) input.addEventListener("input", update);
  const rebuildPick = input ? wireModelPick(form, input, () => (prov ? prov.value : "openrouter"), update) : () => {};
  const onProv = () => {
    if (!prov) return;
    const opt = prov.options[prov.selectedIndex];
    const base = $("[data-base-url]", form);
    if (base) base.hidden = prov.value !== "custom";
    const key = $("[data-key]", form) as HTMLInputElement | null;
    if (key && opt) key.placeholder = opt.dataset.hint || "";
    const link = $("[data-key-link]", form);
    if (link && opt) {
      const u = opt.dataset.keyUrl;
      link.innerHTML = u
        ? `get a key at <a href="${esc(u)}" target="_blank" rel="noopener">${esc(u.replace(/^https:\/\//, ""))}</a>`
        : "";
    }
    const hint = $("[data-model-hint]", form);
    if (hint) hint.textContent = isOR() ? " (openrouter)" : "";
    if (input)
      input.placeholder =
        opt && opt.dataset.example
          ? opt.dataset.example
          : prov.value === "custom"
            ? "model id, as your endpoint names it"
            : "";
    rebuildPick();
    form.querySelectorAll("[data-or-only]").forEach((el) => {
      (el as HTMLElement).hidden = !isOR();
    });
    update();
  };
  if (prov)
    prov.addEventListener("change", () => {
      if (prov) prov.dataset.auto = "0";
      onProv();
    });
  /** a key, pasted or picked, names its provider while the picker is still on auto */
  const follow = (p: string | null) => {
    if (!prov || !auto() || !p || prov.value === p) return;
    prov.value = p;
    onProv();
  };
  const key = $("[data-key]", form) as HTMLInputElement | null;
  if (key) {
    const onKey = () => {
      follow(providerFromKey(key.value));
      rebuildPick();
    };
    key.addEventListener("input", onKey);
    key.addEventListener("change", onKey);
  }
  const pick = $("[data-key-pick]", form) as HTMLSelectElement | null;
  if (pick) {
    pick.addEventListener("change", () => {
      const o = pick.options[pick.selectedIndex];
      follow(o && o.value ? o.dataset.provider || null : null);
      rebuildPick();
    });
    const initOpt = pick.options[pick.selectedIndex];
    if (initOpt && initOpt.value && initOpt.dataset.provider) follow(initOpt.dataset.provider);
  }

  // check for server-rendered refusal or draft error
  const err = form.querySelector("p.error");
  if (err && err.textContent && !form.dataset.errTracked) {
    form.dataset.errTracked = "1";
    if (funnel) trackEnterFunnel("fail", { step: "server", error_code: err.textContent.trim() });
  }

  // funnel telemetry: enter_start on first interaction with the enter form
  const onStart = () => {
    if (form.dataset.enterStarted) return;
    form.dataset.enterStarted = "1";
    if (funnel) trackEnterFunnel("start");
  };
  form.addEventListener("focusin", onStart, { once: true });
  form.addEventListener("input", onStart, { once: true });

  // funnel telemetry: enter_fail on client-side invalid
  form.addEventListener(
    "invalid",
    (e) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      const code = target?.name ? `missing_${target.name}` : "validation_error";
      if (funnel) trackEnterFunnel("fail", { step: "validation", error_code: code });
    },
    true,
  );

  // a contender entered (the enter form alone; manage's edit form posts elsewhere)
  if ((form.getAttribute("action") || "") === "/enter") {
    form.addEventListener("submit", () => {
      track("contender_entered", { provider_kind: prov?.value || "auto" });
      trackEnterFunnel("submit", { provider_kind: prov?.value || "auto" });
    });
  }
  onProv();
}
